import { z } from "zod";
import { GENDER_OPTIONS, validateInternationalId, VALIDATION_KEYS } from "@fysaro/shared";

export const basePatientSchema = z
  .object({
    // --- Datos del perfil (UserProfile) ---
    name: z.string().min(2, VALIDATION_KEYS.PATIENT.NAME_REQUIRED),
    email: z
      .string()
      .email(VALIDATION_KEYS.GENERIC.INVALID_EMAIL)
      .or(z.literal(""))
      .transform((val) => (val === "" ? null : val))
      .optional()
      .nullable(),
    phone: z
      .string()
      .or(z.literal(""))
      .transform((val) => (val === "" ? null : val))
      .optional()
      .nullable(),
    identificationNumber: z
      .string()
      .trim()
      .toUpperCase()
      .or(z.literal(""))
      .transform((val) => (val === "" ? null : val))
      .optional()
      .nullable(),

    forceUpdate: z.boolean().optional().default(false),
    birthDate: z
      .union([
        z.literal(""),
        z.null(),
        z.coerce
          .date()
          .max(new Date(), VALIDATION_KEYS.PATIENT.INVALID_AGE),
      ])
      .transform((val) => (val === "" ? null : val))
      .optional()
      .nullable(),
    gender: z
      .enum(GENDER_OPTIONS)
      .optional()
      .nullable(),
    imageUrl: z.string().url().optional().nullable().default(null),

    // --- Dirección Base (UserProfile) ---
    address: z.string().optional().nullable(),
    street: z.string().optional().nullable(),
    housenumber: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    zipCode: z.string().optional().nullable(),
    country: z.string().default("España"),
    countryCode: z.string().default("ES"),
    latitude: z.number().optional().nullable(),
    longitude: z.number().optional().nullable(),

    // --- Dirección Secundaria / Servicio (Patient) ---
    secondaryAddress: z.string().optional().nullable(),
    secondaryStreet: z.string().optional().nullable(),
    secondaryHousenumber: z.string().optional().nullable(),
    secondaryCity: z.string().optional().nullable(),
    secondaryZipCode: z.string().optional().nullable(),
    secondaryCountry: z.string().default("España"),
    secondaryLatitude: z.number().optional().nullable(),
    secondaryLongitude: z.number().optional().nullable(),

    // --- Datos de Gestión ---
    // memberNumber lo manejamos como number opcional (el UseCase lo autoincrementará)
    memberNumber: z.number().optional().nullable(),
    internalNotes: z
      .string()
      .max(1000, VALIDATION_KEYS.PATIENT.MAX_LENGTH)
      .optional()
      .nullable(),
    tags: z.array(z.string()).default([]),
  })
  .strict();

// --- 2. ESQUEMAS DE OPERACIÓN EXPORTADOS ---
// Para creación completa
export const createPatientSchema = basePatientSchema.superRefine(
  validateInternationalId,
);

// Para actualizaciones (Hace los campos opcionales PRIMERO, y luego refina)
export const updatePatientSchema = basePatientSchema
  .partial()
  .superRefine(validateInternationalId)
  .strict();

const ownProfileAddressSchema = z.object({
  address: z.string().trim().max(300).nullable().optional(),
  street: z.string().trim().max(200).nullable().optional(),
  housenumber: z.string().trim().max(30).nullable().optional(),
  city: z.string().trim().max(120).nullable().optional(),
  zipCode: z.string().trim().max(30).nullable().optional(),
  country: z.string().trim().max(120).nullable().optional(),
  latitude: z.number().finite().nullable().optional(),
  longitude: z.number().finite().nullable().optional(),
});

export const updateOwnPatientUserProfileSchema = ownProfileAddressSchema
  .extend({
    name: z.string().trim().min(2).max(120).optional(),
    phone: z.string().trim().max(50).nullable().optional(),
  })
  .strict();

export const updateOwnPatientProfileSchema = z
  .object({
    secondaryAddress: z.string().trim().max(300).nullable().optional(),
    secondaryStreet: z.string().trim().max(200).nullable().optional(),
    secondaryHousenumber: z.string().trim().max(30).nullable().optional(),
    secondaryCity: z.string().trim().max(120).nullable().optional(),
    secondaryZipCode: z.string().trim().max(30).nullable().optional(),
    secondaryCountry: z.string().trim().max(120).nullable().optional(),
    secondaryLatitude: z.number().finite().nullable().optional(),
    secondaryLongitude: z.number().finite().nullable().optional(),
  })
  .strict();

export const updatePatientImageSchema = z.object({
  imageUrl: z.string().url().nullable(),
});
